import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Box, Dialog, IconButton, Typography, Stack, Alert, Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ScaleIcon from "@mui/icons-material/Scale";
import InventoryIcon from "@mui/icons-material/Inventory";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { QuizButton } from "../../shared/QuizButton";
import { COLORS } from "../constants";
import { useRechargeApi } from "../hooks/useRechargeApi";
import { getProp, findHiddenPriceProduct as findHidden, getPackPriceFromHidden, getBundleVariantId } from "../helpers";
import { getPackPricingConfig, toDailyKey50, type PlanDays } from "@/helpers/plansToPacks";
import { buildMealSelectionsWithBaseline, type MealSelectionWithBaseline } from "@/helpers/mealDistribution";
import { buildVariantIndex } from "../../../utils/recharge";
import type { SubscriptionData } from "../types";
import { QUIZ } from "@/theme/quizTokens";

interface Props {
  open: boolean;
  onClose: () => void;
  sub: SubscriptionData | null;
  products: any[];
  onSuccess: (msg: string) => void;
  onError: (msg: string) => void;
  onRefresh: () => void;
}

type Step = "frequency" | "meals" | "confirm";
type MealSelection = MealSelectionWithBaseline;

function findPlanProduct(products: any[], planDays: number): any | null {
  return products.find((p: any) => {
    const title = String(p.title || "").toLowerCase();
    const handle = String(p.handle || "").toLowerCase();
    if (handle.includes("hidden") || title.includes("hidden")) return false;
    return title.includes(`${planDays} day plan`) || title.includes(`${planDays}-day plan`) || title.includes(`${planDays}day plan`);
  }) || null;
}

function findMealProducts(products: any[]): any[] {
  return products.filter((p: any) => {
    const type = String(p.product_type || "").toLowerCase();
    const tags = String(p.tags || "").toLowerCase();
    const handle = String(p.handle || "").toLowerCase();
    if (handle.includes("hidden")) return false;
    return type === "recipe" || tags.includes("recipe") || tags.includes("meal");
  });
}

function findVariantForPackSize(product: any, packSize: number): any | null {
  if (!product.variants?.length) return null;
  const matchingVariant = product.variants.find((v: any) => {
    const titleMatch = String(v.title || "").match(/(\d+)/);
    return titleMatch && parseInt(titleMatch[1], 10) === packSize;
  });
  return matchingVariant || product.variants[0];
}

function getProductImage(product: any): string | null {
  if (!product) return null;
  if (product.images?.[0]?.src) return product.images[0].src;
  if (typeof product.image === "string") return product.image;
  if (product.image?.src) return product.image.src;
  return null;
}

function buildMealsSummary(meals: MealSelection[], packSize: number): string {
  return meals.filter(m => m.count > 0).map(m => `${m.count}× ${m.productTitle} ${packSize}g`).join(", ");
}

type PlanProductInfo = { days: PlanDays; product: any | null; hiddenPriceProduct: any | null };

export function ChangePlanModal({ open, onClose, sub, products, onSuccess, onError, onRefresh }: Props) {
  const { call } = useRechargeApi();
  const [step, setStep] = useState<Step>("frequency");
  const [selectedPlanDays, setSelectedPlanDays] = useState<PlanDays>(7);
  const [mealSelections, setMealSelections] = useState<MealSelection[]>([]);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const planProducts = useMemo<PlanProductInfo[]>(() => {
    const plans: PlanDays[] = [7, 14, 28];
    return plans.map(days => ({
      days,
      product: findPlanProduct(products, days),
      hiddenPriceProduct: findHidden(products, days),
    }));
  }, [products]);

  const variantIndex = useMemo(() => buildVariantIndex(products), [products]);

  const currentValues = useMemo(() => {
    if (!sub?.properties) return { dailyGrams: 300, packSizeGrams: 400, planDays: 7 as PlanDays };
    const packSizeProp = sub.properties.find(p => p.name === "_paws_pack_size_grams");
    const packSizeGrams = packSizeProp ? parseInt(packSizeProp.value, 10) : 400;
    const planDaysProp = sub.properties.find(p => p.name === "_paws_plan_days");
    let planDays = planDaysProp ? parseInt(planDaysProp.value, 10) : 7;
    if (![7, 14, 28].includes(planDays)) planDays = 7;
    const dogJsonProp = sub.properties.find(p => p.name === "_paws_dog_json");
    let dailyGrams = 300;
    if (dogJsonProp?.value) {
      try { dailyGrams = JSON.parse(dogJsonProp.value).dailyGrams || 300; } catch {}
    }
    return { dailyGrams, dailyKey: toDailyKey50(dailyGrams), packSizeGrams, planDays: planDays as PlanDays };
  }, [sub]);

  const selectedPlanInfo = useMemo(() => planProducts.find(p => p.days === selectedPlanDays) || null, [planProducts, selectedPlanDays]);

  const newPackConfig = useMemo(() => {
    const { dailyKey, packSize, totalPacks } = getPackPricingConfig(currentValues.dailyGrams, selectedPlanDays);
    const hiddenProduct = selectedPlanInfo?.hiddenPriceProduct || null;
    const pricePerPack = getPackPriceFromHidden(hiddenProduct, packSize);
    const totalPrice = totalPacks * pricePerPack;
    const pricePerDay = totalPrice / selectedPlanDays;
    const bundleVariantId = getBundleVariantId(products, selectedPlanDays, packSize);
    return { dailyKey, packSize, totalPacks, pricePerPack, totalPrice, pricePerDay, bundleVariantId };
  }, [selectedPlanDays, currentValues.dailyGrams, selectedPlanInfo, products]);

  const currentPackConfig = useMemo(() => {
    const { packSize, totalPacks } = getPackPricingConfig(currentValues.dailyGrams, currentValues.planDays);
    const currentPlanInfo = planProducts.find(p => p.days === currentValues.planDays);
    const hiddenProduct = currentPlanInfo?.hiddenPriceProduct || null;
    const pricePerPack = getPackPriceFromHidden(hiddenProduct, packSize);
    const totalPrice = totalPacks * pricePerPack;
    return { packSize, totalPacks, totalPrice, pricePerDay: totalPrice / currentValues.planDays };
  }, [currentValues, planProducts]);

  const mealProducts = useMemo(() => findMealProducts(products), [products]);
  const totalMealsSelected = useMemo(() => mealSelections.reduce((s, m) => s + m.count, 0), [mealSelections]);

  const mealState = useMemo(() => {
    const diff = totalMealsSelected - newPackConfig.totalPacks;
    if (diff < 0) return { type: "missing" as const, color: "#B20000", bg: "#FFF0F0", border: "#FFB3B3", message: `Please add ${Math.abs(diff)} more pack${Math.abs(diff) !== 1 ? "s" : ""} to match your plan` };
    if (diff === 0) return { type: "perfect" as const, color: "#2E7D32", bg: "#E5FFEA", border: "#A5D6A7", message: "Perfect! This is the recommended amount" };
    return { type: "extra" as const, color: "#8A6E00", bg: "#FFF9E5", border: "#FFE8A3", message: `You're adding ${diff} extra pack${diff !== 1 ? "s" : ""} beyond the recommended ${newPackConfig.totalPacks}` };
  }, [totalMealsSelected, newPackConfig.totalPacks]);

  const packSizeChanged = newPackConfig.packSize !== currentValues.packSizeGrams;
  const planChanged = selectedPlanDays !== currentValues.planDays;

  const extraMealsPrice = useMemo(() => {
    if (totalMealsSelected <= newPackConfig.totalPacks) return 0;
    return (totalMealsSelected - newPackConfig.totalPacks) * newPackConfig.pricePerPack;
  }, [totalMealsSelected, newPackConfig]);

  const finalTotalPrice = newPackConfig.totalPrice + extraMealsPrice;

  const prevOpenRef = useRef(false);
  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = open;
    if (open && !wasOpen) {
      setStep("frequency");
      setSelectedPlanDays(currentValues.planDays);
      setError(null);
      setSuccess(false);
      setConfirming(false);
      setMealSelections([]);
    }
  }, [open, currentValues.planDays]);

  const handleNext = () => {
    if (step === "frequency") {
      if (!planChanged) { setError("Please select a different delivery frequency"); return; }
      const distributed = buildMealSelectionsWithBaseline(mealProducts, newPackConfig.totalPacks, newPackConfig.packSize, findVariantForPackSize);
      setMealSelections(distributed);
      setError(null);
      setStep("meals");
    } else if (step === "meals") {
      if (totalMealsSelected < newPackConfig.totalPacks) { setError(`Please select at least ${newPackConfig.totalPacks} meals`); return; }
      setError(null);
      setStep("confirm");
    }
  };

  const handleBack = () => {
    setError(null);
    if (step === "meals") setStep("frequency");
    else if (step === "confirm") setStep("meals");
  };

  const handleMealChange = useCallback((variantId: string, delta: number, product: any, variant: any) => {
    setMealSelections(prev => {
      const existing = prev.find(m => m.variantId === variantId);
      if (existing) {
        const newCount = Math.max(0, existing.count + delta);
        return prev.map(m => m.variantId === variantId ? { ...m, count: newCount } : m);
      }
      if (delta > 0) {
        return [...prev, {
          variantId, count: delta, baseline: 0,
          productTitle: product.title, variantTitle: variant.title || "",
          imageUrl: product.images?.[0]?.src || (product.image as any)?.src || null,
          price: parseFloat(String(variant.price || "0")),
        }];
      }
      return prev;
    });
  }, []);

  const handleConfirm = async () => {
    if (!newPackConfig.bundleVariantId) { setError("Unable to find bundle variant."); return; }
    if (!finalTotalPrice || finalTotalPrice <= 0) { setError("Unable to calculate pricing."); return; }
    setConfirming(true);
    setError(null);
    try {
      const activeMeals = mealSelections.filter(m => m.count > 0);
      const mealsSummary = buildMealsSummary(activeMeals, newPackConfig.packSize);
      const meals = activeMeals.map(m => ({
        variantId: String(m.variantId),
        count: m.count,
        productTitle: m.productTitle || "",
        variantTitle: m.variantTitle || "",
        price: newPackConfig.pricePerPack,
      }));
      const res = await call("/recharge/subscription/change-frequency", {
        method: "POST",
        body: {
          subscriptionId: sub!.id,
          newPlanDays: selectedPlanDays,
          newPackSizeGrams: newPackConfig.packSize,
          newVariantId: newPackConfig.bundleVariantId,
          meals,
          newPrice: finalTotalPrice,
          mealsSummary,
        },
      });
      if (!res.ok) throw new Error(res.error || "Failed to change frequency");
      setSuccess(true);
      setTimeout(() => { onSuccess("Delivery frequency updated!"); onClose(); onRefresh(); }, 1200);
    } catch (err: any) {
      setError(err.message || "Failed to change frequency");
    } finally {
      setConfirming(false);
    }
  };

  const formatPrice = (price: number) => `£${price.toFixed(2)}`;
  const steps = [
    { key: "frequency", label: "Select Frequency" },
    { key: "meals", label: "Select Meals" },
    { key: "confirm", label: "Confirm" },
  ];
  const currentStepIndex = steps.findIndex(s => s.key === step);

  return (
    <Dialog
      open={open}
      onClose={confirming ? undefined : onClose}
      maxWidth={step === "confirm" ? "md" : "sm"}
      fullWidth
      PaperProps={{ sx: { borderRadius: "16px", maxHeight: "90vh", display: "flex", flexDirection: "column" } }}
    >
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: 3, borderBottom: "1px solid rgba(0,0,0,.08)", background: QUIZ.color.cream, flexShrink: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {step !== "frequency" && (
            <IconButton onClick={handleBack} disabled={confirming} size="small" sx={{ mr: 0.5 }}>
              <ArrowBackIcon />
            </IconButton>
          )}
          <Box sx={{ width: 44, height: 44, borderRadius: "12px", background: "linear-gradient(135deg, #E8D6FF 0%, #CBA4FF 100%)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <CalendarMonthIcon sx={{ fontSize: 22, color: "#5B2E91" }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 18, fontWeight: 800, color: COLORS.brownDeep }}>
              {steps[currentStepIndex]?.label || "Change Frequency"}
            </Typography>
            <Typography sx={{ fontSize: 12, color: COLORS.brownDeep, opacity: 0.65 }}>
              Step {currentStepIndex + 1} of {steps.length}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} disabled={confirming} sx={{ color: COLORS.brownDeep }}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: "auto", p: 3 }}>
        {success && <Alert severity="success" icon={<CheckCircleIcon />} sx={{ borderRadius: "12px", mb: 2 }}>Delivery frequency updated!</Alert>}
        {error && <Alert severity="error" sx={{ borderRadius: "12px", mb: 2 }}>{error}</Alert>}

        {/* Step 1: Frequency Selection */}
        {step === "frequency" && !success && (
          <Box>
            <Typography sx={{ fontSize: 14, color: COLORS.brownDeep, opacity: 0.75, mb: 3 }}>
              Choose how often deliveries should arrive. Daily serving ({currentValues.dailyKey}g) stays the same.
            </Typography>

            <Stack spacing={1.5} sx={{ mb: 3 }}>
              {planProducts.map(({ days, product, hiddenPriceProduct }) => {
                const isSelected = selectedPlanDays === days;
                const isCurrent = currentValues.planDays === days;
                const productImage = getProductImage(product);
                const productTitle = product?.title || `${days} Day Plan`;
                const { packSize, totalPacks } = getPackPricingConfig(currentValues.dailyGrams, days);
                const pricePerPack = getPackPriceFromHidden(hiddenPriceProduct, packSize);
                const totalPrice = totalPacks * pricePerPack;
                const pricePerDay = totalPrice / days;

                return (
                  <Box
                    key={days}
                    onClick={() => setSelectedPlanDays(days)}
                    sx={{ p: 2, borderRadius: "16px", border: isSelected ? "2px solid #5B2E91" : "1px solid rgba(0,0,0,.08)", background: isSelected ? "#F8F0FF" : QUIZ.color.cream, cursor: "pointer", transition: "all 0.2s ease", "&:hover": { background: isSelected ? "#F8F0FF" : "#F0F0F0" } }}
                  >
                    <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                      <Box sx={{ width: 64, height: 64, borderRadius: "12px", overflow: "hidden", flexShrink: 0, border: isSelected ? "2px solid #5B2E91" : "1px solid rgba(0,0,0,.1)", background: productImage ? "transparent" : QUIZ.color.cream, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        {productImage ? (
                          <Box component="img" src={productImage} alt={productTitle} sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        ) : (
                          <InventoryIcon sx={{ fontSize: 28, color: isSelected ? "#5B2E91" : "#C4A98A" }} />
                        )}
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                          <Typography sx={{ fontSize: 16, fontWeight: 700, color: isSelected ? "#5B2E91" : COLORS.brownDeep }}>{productTitle}</Typography>
                          {isCurrent && <Typography component="span" sx={{ fontSize: 10, fontWeight: 600, color: "#666", px: 0.75, py: 0.25, background: QUIZ.color.cream3, borderRadius: "4px" }}>Current</Typography>}
                        </Box>
                        <Typography sx={{ fontSize: 13, color: COLORS.brownDeep, opacity: 0.65, mb: 0.5 }}>Delivery every {days} days</Typography>
                        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
                          <Typography sx={{ fontSize: 18, fontWeight: 800, color: isSelected ? "#5B2E91" : COLORS.brownDeep }}>{formatPrice(totalPrice)}</Typography>
                          <Typography sx={{ fontSize: 12, color: "#666" }}>({formatPrice(pricePerDay)}/day)</Typography>
                        </Box>
                      </Box>
                      <Box sx={{ width: 24, height: 24, borderRadius: "50%", border: isSelected ? "2px solid #5B2E91" : "2px solid #ccc", background: isSelected ? "#5B2E91" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        {isSelected && <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: QUIZ.color.warmWhite }} />}
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ p: 2, borderRadius: "12px", background: QUIZ.color.cream, border: "1px solid rgba(0,0,0,.06)" }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: COLORS.brownDeep, mb: 1.5 }}>Your {selectedPlanDays}-Day Plan will include:</Typography>
              <Stack spacing={1}>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}><ScaleIcon sx={{ fontSize: 18, color: "#5B2E91" }} /><Typography sx={{ fontSize: 14, color: COLORS.brownDeep }}>Pack size</Typography></Box>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: COLORS.brownDeep }}>
                    {newPackConfig.packSize}g
                    {packSizeChanged && <Typography component="span" sx={{ fontSize: 12, color: "#666", ml: 1 }}>(was {currentValues.packSizeGrams}g)</Typography>}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}><InventoryIcon sx={{ fontSize: 18, color: "#5B2E91" }} /><Typography sx={{ fontSize: 14, color: COLORS.brownDeep }}>Packs per delivery</Typography></Box>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: COLORS.brownDeep }}>{newPackConfig.totalPacks} packs</Typography>
                </Box>
                <Divider />
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography sx={{ fontSize: 14, color: COLORS.brownDeep }}>Total per delivery</Typography>
                  <Typography sx={{ fontSize: 18, fontWeight: 800, color: "#5B2E91" }}>{formatPrice(newPackConfig.totalPrice)}</Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography sx={{ fontSize: 13, color: COLORS.brownDeep, opacity: 0.65 }}>Per day</Typography>
                  <Typography sx={{ fontSize: 13, color: COLORS.brownDeep, opacity: 0.65 }}>{formatPrice(newPackConfig.pricePerDay)}/day</Typography>
                </Box>
              </Stack>
            </Box>

            {packSizeChanged && (
              <Alert severity="info" sx={{ mt: 2, borderRadius: "12px" }}>
                Pack size will change from {currentValues.packSizeGrams}g to {newPackConfig.packSize}g. You'll need to re-select meals.
              </Alert>
            )}
          </Box>
        )}

        {/* Step 2: Meal Selection */}
        {step === "meals" && !success && (
          <Box>
            <Box sx={{ mb: 2, p: 1.5, borderRadius: "12px", background: mealState.bg, border: `1px solid ${mealState.border}` }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: mealState.color, textAlign: "center" }}>{mealState.message}</Typography>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography sx={{ fontSize: 14, color: COLORS.brownDeep, opacity: 0.75 }}>Select meals ({newPackConfig.totalPacks} recommended)</Typography>
              <Box sx={{ px: 1.5, py: 0.5, borderRadius: "8px", background: mealState.type === "missing" ? "#FFF3CD" : mealState.type === "perfect" ? "#E5FFEA" : "#FFF9E5", color: mealState.type === "missing" ? "#856404" : mealState.type === "perfect" ? "#2E7D32" : "#8A6E00", fontWeight: 700, fontSize: 13 }}>
                {totalMealsSelected} / {newPackConfig.totalPacks}
              </Box>
            </Box>

            <Stack spacing={1.5}>
              {mealProducts.map((product: any) => {
                const variant = findVariantForPackSize(product, newPackConfig.packSize);
                if (!variant) return null;
                const variantId = String(variant.id);
                const selection = mealSelections.find(m => m.variantId === variantId);
                const count = selection?.count || 0;
                const imageUrl = variantIndex.get(variantId)?.imageUrl;

                return (
                  <Box key={product.id} sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 1.5, borderRadius: "12px", border: count > 0 ? "2px solid #5B2E91" : "1px solid rgba(0,0,0,.08)", background: count > 0 ? "#F8F0FF" : QUIZ.color.cream }}>
                    <Box sx={{ width: 56, height: 56, borderRadius: "8px", overflow: "hidden", flexShrink: 0, background: QUIZ.color.cream, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      {imageUrl ? (
                        <Box component="img" src={imageUrl} alt={product.title} sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      ) : (
                        <RestaurantIcon sx={{ fontSize: 24, color: "#C4A98A" }} />
                      )}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 700, color: COLORS.brownDeep, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{product.title}</Typography>
                      <Typography sx={{ fontSize: 12, color: COLORS.brownDeep, opacity: 0.65 }}>{newPackConfig.packSize}g pack</Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
                      <IconButton size="small" onClick={() => handleMealChange(variantId, -1, product, variant)} disabled={count <= 0} sx={{ width: 32, height: 32, border: "1px solid rgba(0,0,0,.12)", "&:disabled": { opacity: 0.3 } }}>
                        <RemoveIcon fontSize="small" />
                      </IconButton>
                      <Typography sx={{ width: 32, textAlign: "center", fontWeight: 700, fontSize: 16 }}>{count}</Typography>
                      <IconButton size="small" onClick={() => handleMealChange(variantId, 1, product, variant)} sx={{ width: 32, height: 32, background: "#5B2E91", color: QUIZ.color.white, "&:hover": { background: "#472172" } }}>
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                );
              })}
            </Stack>

            {extraMealsPrice > 0 && (
              <Alert severity="info" sx={{ mt: 2, borderRadius: "12px" }}>Extra meals will add {formatPrice(extraMealsPrice)} to the order</Alert>
            )}
          </Box>
        )}

        {/* Step 3: Confirmation */}
        {step === "confirm" && !success && (
          <Box>
            <Typography sx={{ fontSize: 14, color: COLORS.brownDeep, opacity: 0.75, mb: 3 }}>Review changes before confirming.</Typography>
            <Stack spacing={2}>
              <Box sx={{ p: 2, borderRadius: "12px", background: QUIZ.color.cream, border: "1px solid rgba(0,0,0,.06)" }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: COLORS.brownDeep, opacity: 0.65, mb: 1 }}>DELIVERY FREQUENCY</Typography>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Typography sx={{ fontSize: 14, color: COLORS.brownDeep }}>{currentValues.planDays} Day Plan &rarr; {selectedPlanDays} Day Plan</Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#5B2E91" }}>Every {selectedPlanDays} days</Typography>
                </Box>
              </Box>
              <Box sx={{ p: 2, borderRadius: "12px", background: QUIZ.color.cream, border: "1px solid rgba(0,0,0,.06)" }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: COLORS.brownDeep, opacity: 0.65, mb: 1 }}>PACK DETAILS</Typography>
                <Typography sx={{ fontSize: 14, color: COLORS.brownDeep }}>
                  {newPackConfig.packSize}g packs × {totalMealsSelected}
                  {totalMealsSelected > newPackConfig.totalPacks && <Typography component="span" sx={{ fontSize: 12, color: "#8A6E00", ml: 1 }}>(+{totalMealsSelected - newPackConfig.totalPacks} extra)</Typography>}
                </Typography>
              </Box>
              <Box sx={{ p: 2, borderRadius: "12px", background: QUIZ.color.cream, border: "1px solid rgba(0,0,0,.06)" }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: COLORS.brownDeep, opacity: 0.65, mb: 1 }}>MEALS ({totalMealsSelected} packs)</Typography>
                <Stack spacing={0.5}>
                  {mealSelections.filter(m => m.count > 0).map(m => (
                    <Typography key={m.variantId} sx={{ fontSize: 13, color: COLORS.brownDeep }}>{m.count}× {m.productTitle}</Typography>
                  ))}
                </Stack>
              </Box>
              <Box sx={{ p: 2, borderRadius: "12px", background: "#F8F0FF", border: "2px solid #D4B8FF" }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#5B2E91", mb: 1 }}>NEW PRICE</Typography>
                {extraMealsPrice > 0 && (
                  <Box sx={{ mb: 1 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography sx={{ fontSize: 13, color: COLORS.brownDeep, opacity: 0.75 }}>Base plan ({newPackConfig.totalPacks} packs)</Typography>
                      <Typography sx={{ fontSize: 13, color: COLORS.brownDeep }}>{formatPrice(newPackConfig.totalPrice)}</Typography>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography sx={{ fontSize: 13, color: "#8A6E00" }}>Extra packs ({totalMealsSelected - newPackConfig.totalPacks})</Typography>
                      <Typography sx={{ fontSize: 13, color: "#8A6E00" }}>+{formatPrice(extraMealsPrice)}</Typography>
                    </Box>
                    <Divider sx={{ my: 1 }} />
                  </Box>
                )}
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography sx={{ fontSize: 14, color: COLORS.brownDeep }}>Per delivery</Typography>
                  <Typography sx={{ fontSize: 20, fontWeight: 800, color: "#5B2E91" }}>{formatPrice(finalTotalPrice)}</Typography>
                </Box>
                <Typography sx={{ fontSize: 12, color: COLORS.brownDeep, opacity: 0.65, textAlign: "right" }}>({formatPrice(finalTotalPrice / selectedPlanDays)}/day)</Typography>
              </Box>
              {currentPackConfig.totalPrice > 0 && (
                <Box sx={{ p: 1.5, borderRadius: "8px", background: finalTotalPrice > currentPackConfig.totalPrice ? "#FFF9E5" : "#E5FFEA", textAlign: "center" }}>
                  <Typography sx={{ fontSize: 13, color: COLORS.brownDeep }}>
                    {finalTotalPrice > currentPackConfig.totalPrice
                      ? `This is ${formatPrice(finalTotalPrice - currentPackConfig.totalPrice)} more per delivery`
                      : finalTotalPrice < currentPackConfig.totalPrice
                      ? `Save ${formatPrice(currentPackConfig.totalPrice - finalTotalPrice)} per delivery`
                      : "Same price per delivery"}
                  </Typography>
                </Box>
              )}
            </Stack>
          </Box>
        )}
      </Box>

      {/* Footer */}
      {!success && (
        <Box sx={{ p: 3, borderTop: "1px solid rgba(0,0,0,.08)", background: QUIZ.color.cream, flexShrink: 0 }}>
          <Stack direction="row" spacing={1.5}>
            <QuizButton variant="secondary" onClick={step === "frequency" ? onClose : handleBack} disabled={confirming} fullWidth>
              {step === "frequency" ? "Cancel" : "Back"}
            </QuizButton>
            <QuizButton
              variant="primary"
              onClick={step === "confirm" ? handleConfirm : handleNext}
              disabled={confirming || (step === "frequency" && !planChanged) || (step === "meals" && totalMealsSelected < newPackConfig.totalPacks)}
              fullWidth
            >
              {confirming ? "Updating..." : step === "confirm" ? "Confirm Changes" : "Next"}
            </QuizButton>
          </Stack>
        </Box>
      )}
    </Dialog>
  );
}
